import { LucideWandSparkles } from 'lucide-react'
import React, { Dispatch, SetStateAction, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { ExtractQuoteItemsLoader } from './components/ExtractQuoteItemsLoader'
import { Button } from '../../../../../../../shadcn/components/ui/button'
import { errorToast, successToast } from '../../../../../../../utils'
import { formatGqlError } from '../../../../../../../utils/format-error'
import { SimpleModal } from '../../../../../../atoms'
import { IProposalInteractionsProps } from '../../../../../../organisms/proposal/Proposal'

interface ExtractQuoteItemsButtonProps {
  ownerActions: Pick<
    NonNullable<IProposalInteractionsProps['ownerActions']>,
    'ownerExtractQuoteItemsFromPdf'
  >
  clientProposalId: string
  hasQuoteItems: boolean
}
export const ExtractQuoteItemsButton = ({
  clientProposalId,
  ownerActions,
  hasQuoteItems,
}: ExtractQuoteItemsButtonProps) => {
  const [isExtractionModalOpen, setIsExtractionModalOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleOnOpen = () => {
    setErrorMessage('')
    setIsExtractionModalOpen(true)
  }

  const handleOnClose = () => {
    setIsExtractionModalOpen(false)
  }

  const handleOnExtractQuotationItems = async () => {
    handleOnOpen()

    const response = await ownerActions.ownerExtractQuoteItemsFromPdf?.callback(
      { clientProposalId },
    )

    const success = response?.ownerExtractQuoteItemsFromPdf?.success
    const error = response?.ownerExtractQuoteItemsFromPdf?.error

    const handleError = () => {
      handleOnError({ error, setErrorMessage, handleOnClose })
    }

    if (success) {
      if (error) {
        handleError()
      } else {
        successToast(
          <FormattedMessage
            id='ui.ExtractQuoteItemsButton.toast.success'
            defaultMessage='Extraction réussie !'
          />,
        )

        handleOnClose()
      }
    } else {
      handleError()
    }
  }

  const isLoading =
    ownerActions.ownerExtractQuoteItemsFromPdf?.isLoading ?? false

  return (
    <>
      <>
        <Button
          variant='outline'
          className='flex gap-2'
          onClick={hasQuoteItems ? handleOnOpen : handleOnExtractQuotationItems}
          disabled={isLoading}
        >
          <LucideWandSparkles size={16} />

          {hasQuoteItems ? (
            <FormattedMessage
              id='ui.ExtractQuoteItemsButton.button.restart'
              defaultMessage="Relancer l'extraction"
            />
          ) : (
            <FormattedMessage
              id='ui.ExtractQuoteItemsButton.button.start'
              defaultMessage='Extraire le devis'
            />
          )}
        </Button>
      </>

      <SimpleModal
        hasError={Boolean(errorMessage)}
        errorMessage={errorMessage}
        isLoading={isLoading}
        loader={<ExtractQuoteItemsLoader />}
        isOpen={isExtractionModalOpen}
        name='extraction-quote-items-modal'
        onClose={isLoading ? null : handleOnClose}
        style={{ height: isLoading ? 400 : 260, width: isLoading ? 675 : 600 }}
        content={{
          body: (
            <div className='flex h-full flex-col justify-center gap-4 p-4 text-sm'>
              <p>
                <FormattedMessage
                  id='ui.ExtractQuoteItemsButton.modal.body.info'
                  defaultMessage='Les prestations actuelles seront supprimées et seront remplacées par celles issues de la nouvelle
                extraction du devis.'
                />
              </p>

              <p>
                <FormattedMessage
                  id='ui.ExtractQuoteItemsButton.modal.body.question'
                  defaultMessage='Souhaitez-vous continuer ?'
                />
              </p>
            </div>
          ),
          header: isLoading ? null : (
            <p className='text-start font-semibold'>
              <FormattedMessage
                id='ui.ExtractQuoteItemsButton.modal.header'
                defaultMessage="Relancer l'extraction du devis ?"
              />
            </p>
          ),
          footer: (
            <div className='flex justify-between gap-4'>
              <Button
                onClick={handleOnClose}
                className='flex-1'
                variant='outline'
              >
                <FormattedMessage
                  id='ui.ExtractQuoteItemsButton.modal.cancel'
                  defaultMessage='Annuler'
                />
              </Button>

              <Button
                onClick={handleOnExtractQuotationItems}
                variant='outline'
                className='flex-1 border-red-500 text-red-500'
              >
                <FormattedMessage
                  id='ui.ExtractQuoteItemsButton.modal.confirm'
                  defaultMessage="Supprimer et relancer l'extraction"
                />
              </Button>
            </div>
          ),
        }}
      />
    </>
  )
}

const handleOnError = ({
  error,
  setErrorMessage,
  handleOnClose,
}: {
  error: string | null | undefined
  setErrorMessage: Dispatch<SetStateAction<string>>
  handleOnClose: () => void
}) => {
  if (error === 'QUOTATION_FILE_NOT_FOUND') {
    handleOnClose()

    return errorToast(
      <FormattedMessage
        key='error'
        id='ui.ExtractQuoteItemsButton.toast.error.fileNotFound'
        defaultMessage="Aucun devis n'à été trouvé"
      />,
    )
  } else if (error === 'COULD_NOT_UPLOAD_FILE') {
    handleOnClose()

    return errorToast(
      <FormattedMessage
        key='error'
        id='ui.ExtractQuoteItemsButton.toast.error.couldNotUploadFile'
        defaultMessage="Une erreur s'est produite lors de l'upload du fichier"
      />,
    )
  } else if (error === 'NO_ITEM_FOUND') {
    handleOnClose()

    return errorToast(
      <FormattedMessage
        key='error'
        id='ui.ExtractQuoteItemsButton.toast.error.noItemFound'
        defaultMessage="Aucune prestation n'à été trouvée"
      />,
    )
  } else {
    const errorMsg = formatGqlError(error)

    setErrorMessage(
      typeof errorMsg === 'string'
        ? errorMsg
        : (errorMsg?.message ?? 'UNKNOWN_ERROR'),
    )
  }
}
